import React from "react";

type WSStage = 0 | 1 | 2 | 3; // CONNECTING | OPEN | CLOSING | CLOSED

export function useWebSocketWithStage(
  url?: string,                 // full WS URL (may be undefined until ready)
  query_token?: string,         // only used if no subprotocols provided
  protocols?: string | string[] // prefer a single subprotocol: `bearer.<JWT>`
) {
  const wsRef = React.useRef<WebSocket | null>(null);
  const [readyState, setReadyState] = React.useState<WSStage>(WebSocket.CLOSED as WSStage);
  const [stageTrigger, setStageTrigger] = React.useState<number>(0);
  const [reconnectNonce, setReconnectNonce] = React.useState(0);

  const resolvedProtocols = React.useMemo(() => {
    if (!protocols) return undefined;
    const arr = Array.isArray(protocols) ? protocols : [protocols];
    const clean = arr.filter(p => typeof p === "string" && p.trim().length > 0);
    return clean.length ? clean : undefined;
  }, [protocols]);

  const resolvedUrl = React.useMemo(() => {
    if (!url || /\/ws\/thinking\/(null|undefined)\/(null|undefined)$/.test(url)) return undefined;

    if (query_token && !resolvedProtocols) {
      const u = new URL(url, window.location.href);
      u.searchParams.set("query_token", `bearer.${query_token}`);
      return u.toString();
    }
    return url;
  }, [url, query_token, resolvedProtocols]);

  React.useEffect(() => {
    if (!resolvedUrl) return;

    let closedByUser = false;
    let reconnectTimer: number | undefined;

    const openSocket = () => {
      const socket = resolvedProtocols
        ? new WebSocket(resolvedUrl, resolvedProtocols)
        : new WebSocket(resolvedUrl);

      wsRef.current = socket;
      setReadyState(socket.readyState as WSStage);

      const handleOpen = () => {
        setReadyState(WebSocket.OPEN as WSStage);
        setStageTrigger(WebSocket.OPEN);
      };

      const handleClose = () => {
        setReadyState(WebSocket.CLOSED as WSStage);
        setStageTrigger(WebSocket.CLOSED);
        if (!closedByUser) {
          // simple backoff up to ~5s
          const attempt = Math.min(5, reconnectNonce + 1);
          reconnectTimer = window.setTimeout(
            () => setReconnectNonce(n => n + 1),
            attempt * 1000
          ) as unknown as number;
        }
      };

      const handleError = () => { /* optional log */ };

      socket.addEventListener("open", handleOpen);
      socket.addEventListener("close", handleClose);
      socket.addEventListener("error", handleError);

      return () => {
        closedByUser = true;
        socket.removeEventListener("open", handleOpen);
        socket.removeEventListener("close", handleClose);
        socket.removeEventListener("error", handleError);
        try { socket.close(); } catch {}
        if (wsRef.current === socket) wsRef.current = null;
        if (reconnectTimer) window.clearTimeout(reconnectTimer);
      };
    };

    return openSocket();
    // Reconnect when URL/protocols change OR nonce bumps
  }, [resolvedUrl, resolvedProtocols, reconnectNonce]);

  return { wsRef, readyState, stageTrigger };
}
